const EDIT_LABEL = 'Update design';

const getEditReference = (search) => {
  const params = new URLSearchParams(search || '');
  if (params.get('bm_edit') !== '1') return null;

  const lineItemKey = (params.get('line_item_key') || params.get('edit_cart_item') || '').trim();
  const parsedLine = Number.parseInt(params.get('line_item') || '', 10);
  const lineItemIndex = Number.isFinite(parsedLine) && parsedLine > 0 ? parsedLine : 0;
  if (!lineItemKey && !lineItemIndex) return null;

  return { lineItemKey, lineItemIndex };
};

const getProductHandle = (pathname) => {
  const parts = String(pathname || '').split('/').filter(Boolean);
  return parts[0] === 'products' ? parts[1] || '' : '';
};

const getSubmittedProperties = (formData) => {
  const properties = {};
  for (const [name, value] of formData.entries()) {
    if (!name.startsWith('properties[') || !name.endsWith(']')) continue;
    const key = name.slice('properties['.length, -1).trim();
    if (!key) continue;

    if (typeof File === 'function' && value instanceof File) {
      if (value.name) properties[key] = value.name;
      continue;
    }
    properties[key] = String(value);
  }
  return properties;
};

const findEditableItem = (items, reference) => {
  if (reference.lineItemKey) {
    const exactMatch = items.find((item) => String(item?.key || '') === reference.lineItemKey);
    if (exactMatch) return exactMatch;
  }
  return reference.lineItemIndex ? items[reference.lineItemIndex - 1] || null : null;
};

export function hasIncompleteBuilderFigure(form) {
  const builder = form?.querySelector?.('.smart-personalization, #smartPersonalization');
  if (!builder) return false;

  const figureBlocks = [...(form.querySelectorAll?.('.figure-block') || [])];
  if (!figureBlocks.length) return true;

  return figureBlocks.some((block) =>
    ['face', 'hair', 'top', 'bottom'].some((part) => {
      const field = block.querySelector?.(`.fig-${part}`);
      return !field || !String(field.value || '').trim();
    }),
  );
}

const hasIncompleteFrameFields = (form) => {
  if (!form?.querySelector?.('.frame-section')) return false;
  return !String(form.querySelector('#frameColor')?.value || '').trim()
    || !String(form.querySelector('#templateNumber')?.value || '').trim();
};

export async function updateExistingCartLine({
  search,
  pathname,
  formData,
  fetchImpl = globalThis.fetch,
}) {
  const reference = getEditReference(search);
  if (!reference || typeof fetchImpl !== 'function') return { updated: false, reason: 'not-edit-mode' };

  const cartResponse = await fetchImpl('/cart.js', { cache: 'no-store' });
  if (!cartResponse.ok) throw new Error(`Cart lookup failed: ${cartResponse.status}`);

  const cart = await cartResponse.json();
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const item = findEditableItem(items, reference);
  if (!item) throw new Error('The design being edited is no longer in the cart.');

  const productHandle = getProductHandle(pathname);
  if (productHandle && item.handle && productHandle !== String(item.handle)) {
    throw new Error('The edit link does not match this product.');
  }

  const quantity = Number.parseInt(String(item.quantity || ''), 10) || 1;
  const payload = {
    id: String(item.key || reference.lineItemKey),
    quantity,
    properties: getSubmittedProperties(formData),
  };

  const updateResponse = await fetchImpl('/cart/change.js', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!updateResponse.ok) throw new Error(`Cart update failed: ${updateResponse.status}`);

  return { updated: true, cart: await updateResponse.json() };
}

const getSubmitButton = (form) =>
  form.querySelector(
    'button[type="submit"], input[type="submit"], .add-to-cart-button.p_a_t_c, button.p_a_t_c, input.p_a_t_c',
  );

const setButtonState = (button, { busy = false, label = EDIT_LABEL } = {}) => {
  if (!button) return;
  button.disabled = busy;
  button.setAttribute('aria-busy', String(busy));
  button.setAttribute('aria-label', label);

  const text = button.querySelector?.('.add-to-cart-text__content');
  if (text) text.textContent = label;
  if (typeof HTMLInputElement === 'function' && button instanceof HTMLInputElement) button.value = label;
};

const updateFromForm = async (form, event, preferredButton = null) => {
  const reference = getEditReference(window.location.search);
  if (!reference) return;
  if (!(form instanceof HTMLFormElement) || !form.closest('product-form-component')) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation?.();
  if (form.dataset.tfrCartEditBusy === 'true') return;

  form.dataset.tfrCartEditBusy = 'true';
  const button = preferredButton || getSubmitButton(form);
  setButtonState(button, { busy: true, label: 'Updating design…' });

  try {
    await updateExistingCartLine({
      search: window.location.search,
      pathname: window.location.pathname,
      formData: new FormData(form),
    });
    window.localStorage.setItem('edit_cart_refresh', '1');
    window.location.assign('/cart?edit_refresh=1');
  } catch (error) {
    console.error('Direct cart design update failed', error);
    delete form.dataset.tfrCartEditBusy;
    setButtonState(button);
    window.alert('We could not update this design. Please return to the cart and choose Edit design again.');
  }
};

const handleEditClick = (event) => {
  if (!getEditReference(window.location.search)) return;
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest(
    'product-form-component button[type="submit"], product-form-component input[type="submit"], product-form-component .add-to-cart-button.p_a_t_c, product-form-component button.p_a_t_c, product-form-component input.p_a_t_c',
  );
  if (!button || button.disabled) return;

  const form = button.form || button.closest('form');
  if (!(form instanceof HTMLFormElement)) return;
  if (hasIncompleteBuilderFigure(form) || hasIncompleteFrameFields(form)) return;
  if (!form.reportValidity()) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
    return;
  }

  void updateFromForm(form, event, button);
};

const handleEditSubmit = (event) => {
  void updateFromForm(event.target, event);
};

if (typeof document !== 'undefined') {
  document.addEventListener('click', handleEditClick, true);
  document.addEventListener('submit', handleEditSubmit, true);
}
